import type {
  Business,
  Invoice,
  MarketQuote,
  Order,
  OrderLine,
  Party,
  Payment,
  Product,
  PurchaseTask,
  Route,
  Session,
  Shipment,
  StockLot,
  Thread,
  Message,
  Appointment,
  Membership,
} from '@prody/shared';

/* ------------------------------------------------------------------ *
 * Seed data.
 *
 * Deliberately specific rather than lorem-ipsum: real Mercabarna stand
 * numbers, real Catalan farm names, restaurant order patterns that
 * actually occur (a hotel ordering 40 kg of tomatoes and 3 kg of
 * chanterelles on the same note). Generic sample data hides layout
 * problems — "Product 1 / Product 2" never wraps, never has a long
 * Spanish name, and never has a calibre.
 *
 * This module is the standalone backend. It is what makes the app run
 * with `pnpm dev` before the NestJS side exists, and it doubles as
 * the fixture set for tests and for the offline demo.
 * ------------------------------------------------------------------ */

const iso = (daysFromNow: number, hour = 9, minute = 0): string => {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() + daysFromNow);
  d.setUTCHours(hour, minute, 0, 0);
  return d.toISOString();
};

const day = (offset: number): string => {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() + offset);
  return d.toISOString().slice(0, 10);
};

const audit = {
  createdAt: iso(-30),
  createdBy: null,
  updatedAt: iso(-1),
  updatedBy: null,
  deletedAt: null,
} as const;

const EUR = 'EUR';
const m = (major: number) => ({ minor: Math.round(major * 100), currency: EUR });

/* ------------------------------------------------------------------ *
 * Tax
 * ------------------------------------------------------------------ */

const SPANISH_IVA = {
  id: 'es-iva',
  label: 'IVA (España)',
  country: 'ES',
  pricesIncludeTax: false,
  defaultRateKey: 'super-reduced',
  rounding: 'per-line' as const,
  rates: [
    { key: 'standard', label: 'General 21%', bps: 2100, surchargeBps: 520 },
    { key: 'reduced', label: 'Reducido 10%', bps: 1000, surchargeBps: 140 },
    { key: 'super-reduced', label: 'Superreducido 4%', bps: 400, surchargeBps: 50 },
    { key: 'zero', label: 'Exento 0%', bps: 0, surchargeBps: 0 },
    // Transitional rates that live production data still carries.
    { key: 'iva-5', label: 'Reducido 5%', bps: 500, surchargeBps: 0 },
    { key: 'iva-16', label: 'Histórico 16%', bps: 1600, surchargeBps: 0 },
  ],
};

/* ------------------------------------------------------------------ *
 * Businesses
 * ------------------------------------------------------------------ */

export const BUSINESSES: Business[] = [
  {
    ...audit,
    id: 'biz_verdal' as Business['id'],
    slug: 'verdal',
    name: 'Prody Admin',
    legalName: 'Prody Admin',
    taxId: 'B66123456',
    kind: 'wholesaler',
    logoUrl: null,
    traitPack: 'fresh-food',
    status: 'active',
    enabledModules: [
      'dashboard', 'orders', 'grouping', 'market', 'purchasing', 'suppliers',
      'logistics', 'deliveries', 'catalog', 'products', 'stock', 'contacts',
      'messaging', 'schedule', 'invoices', 'payments', 'analytics', 'team',
      'settings',
    ],
    settings: {
      currency: EUR,
      timezone: 'Europe/Madrid',
      formatLocale: 'es-ES',
      defaultLocale: 'en',
      enabledLocales: ['en', 'es', 'ru'],
      taxScheme: SPANISH_IVA,
      weekStart: 'mon',
      // The market opens at 03:00; anything booked before that belongs
      // to the previous trading day.
      dayStartsAt: '03:00',
      orderCutoff: '18:00',
      unitsInUse: ['kg', 'g', 'pc', 'box', 'crate', 'l'],
      handlingClasses: ['ambient', 'chilled', 'frozen', 'fresh', 'live'],
      weightPrecisionG: 10,
    },
  },
  {
    ...audit,
    id: 'biz_canpuig' as Business['id'],
    slug: 'can-puig',
    name: 'Can Puig',
    legalName: 'Mas Can Puig SCP',
    taxId: 'J17998877',
    kind: 'producer',
    logoUrl: null,
    traitPack: 'fresh-food',
    status: 'active',
    // A farm does not need routing, invoicing in two directions, or a
    // market board. The rail simply renders fewer entries.
    enabledModules: [
      'dashboard', 'orders', 'catalog', 'products', 'stock', 'contacts',
      'messaging', 'invoices', 'settings',
    ],
    settings: {
      currency: EUR,
      timezone: 'Europe/Madrid',
      formatLocale: 'ca-ES',
      defaultLocale: 'es',
      enabledLocales: ['es', 'en'],
      taxScheme: SPANISH_IVA,
      weekStart: 'mon',
      dayStartsAt: '00:00',
      orderCutoff: '16:00',
      unitsInUse: ['kg', 'crate', 'pc'],
      handlingClasses: ['ambient', 'fresh'],
      weightPrecisionG: 100,
    },
  },
];

/* ------------------------------------------------------------------ *
 * Session
 * ------------------------------------------------------------------ */

export const SESSION: Session = {
  user: {
    ...audit,
    id: 'usr_marta' as Session['user']['id'],
    email: 'marta@verdal.es',
    name: 'Marta Ribó',
    phone: '+34600112233',
    avatarUrl: null,
    locale: null,
    lastSeenAt: iso(0),
  },
  memberships: [
    { business: BUSINESSES[0]!, role: 'owner', extraPermissions: [] },
    { business: BUSINESSES[1]!, role: 'manager', extraPermissions: [] },
  ],
};

export const TEAM: (Membership & { name: string; email: string })[] = [
  { ...audit, businessId: BUSINESSES[0]!.id, userId: 'usr_marta' as never, role: 'owner', extraPermissions: [], status: 'active', name: 'Marta Ribó', email: 'marta@verdal.es' },
  { ...audit, businessId: BUSINESSES[0]!.id, userId: 'usr_jordi' as never, role: 'buyer', extraPermissions: [], status: 'active', name: 'Jordi Sales', email: 'jordi@verdal.es' },
  { ...audit, businessId: BUSINESSES[0]!.id, userId: 'usr_lena' as never, role: 'sales', extraPermissions: ['analytics:export'], status: 'active', name: 'Lena Kovalenko', email: 'lena@verdal.es' },
  { ...audit, businessId: BUSINESSES[0]!.id, userId: 'usr_paco' as never, role: 'driver', extraPermissions: [], status: 'active', name: 'Paco Ortega', email: 'paco@verdal.es' },
  { ...audit, businessId: BUSINESSES[0]!.id, userId: 'usr_rosa' as never, role: 'accountant', extraPermissions: [], status: 'active', name: 'Rosa Camps', email: 'rosa@verdal.es' },
  { ...audit, businessId: BUSINESSES[0]!.id, userId: 'usr_nuria' as never, role: 'warehouse', extraPermissions: [], status: 'invited', name: 'Núria Bosch', email: 'nuria@verdal.es' },
];

/* ------------------------------------------------------------------ *
 * Parties
 * ------------------------------------------------------------------ */

const terms = (netDays: number, limit: number | null = null) => ({
  netDays,
  creditLimitMinor: limit === null ? null : limit * 100,
  blockOnOverLimit: false,
  method: 'transfer' as const,
});

export const PARTIES: Party[] = [
  {
    ...audit, id: 'pty_sorli' as Party['id'], businessId: BUSINESSES[0]!.id, custom: {},
    kind: 'company', roles: ['customer'], code: 'SORLI', name: 'Restaurant Sorlí',
    legalName: 'Sorlí Gastronomia SL', taxId: 'B60112233', locale: 'es',
    channels: [
      { kind: 'whatsapp', address: '+34677001122', label: 'Cocina', primary: true, verifiedAt: iso(-90), optedOut: false },
      { kind: 'email', address: 'compras@sorli.cat', label: null, primary: false, verifiedAt: iso(-90), optedOut: false },
    ],
    people: [
      { id: 'p1', name: 'Aleix Ferrer', role: 'Jefe de cocina', channels: [], isDecisionMaker: true, notes: 'Prefiere nota de voz antes de las 07:00.' },
      { id: 'p2', name: 'Bea Roig', role: 'Recepción de género', channels: [], isDecisionMaker: false, notes: null },
    ],
    addresses: [{
      id: 'a1', label: 'Local', line1: 'Carrer de Provença 288', line2: null, city: 'Barcelona',
      postcode: '08008', region: 'Catalunya', country: 'ES', lat: 41.394, lng: 2.161,
      accessNotes: 'Entrada de servicio por el chaflán. Timbre "cocina".',
    }],
    deliveryPreferences: [{ addressId: 'a1', weekdays: ['mon', 'tue', 'wed', 'thu', 'fri'], window: { from: '07:00', to: '10:00' }, strict: true }],
    paymentTerms: terms(30, 6000), priceListId: 'pl_restaurants', ownerId: 'usr_lena' as never,
    referredByPartyId: 'pty_amb_carla' as Party['id'], supplier: null, ambassador: null,
    tags: ['restaurante', 'centro'], notes: null, status: 'active',
  },
  {
    ...audit, id: 'pty_arts' as Party['id'], businessId: BUSINESSES[0]!.id, custom: {},
    kind: 'company', roles: ['customer'], code: 'HOTEL-ARTS', name: 'Hotel Arts — Cocina central',
    legalName: 'Arts Hotelera SA', taxId: 'A08334455', locale: 'en',
    channels: [{ kind: 'email', address: 'fb.purchasing@arts.example', label: 'F&B', primary: true, verifiedAt: iso(-200), optedOut: false }],
    people: [{ id: 'p1', name: 'Sofia Marchetti', role: 'F&B purchasing', channels: [], isDecisionMaker: true, notes: null }],
    addresses: [{
      id: 'a1', label: 'Muelle de carga', line1: "Carrer de la Marina 19", line2: 'Muelle -2', city: 'Barcelona',
      postcode: '08005', region: 'Catalunya', country: 'ES', lat: 41.386, lng: 2.196,
      accessNotes: 'Altura máxima 2,9 m. Pase de proveedor en garita.',
    }],
    deliveryPreferences: [{ addressId: 'a1', weekdays: ['mon', 'wed', 'fri'], window: { from: '06:00', to: '08:00' }, strict: true }],
    paymentTerms: terms(60, 25000), priceListId: 'pl_hotels', ownerId: 'usr_lena' as never,
    referredByPartyId: null, supplier: null, ambassador: null,
    tags: ['hotel', '5*'], notes: 'Auditan temperatura en recepción. Adjuntar registro de cadena de frío.', status: 'active',
  },
  {
    ...audit, id: 'pty_bodega' as Party['id'], businessId: BUSINESSES[0]!.id, custom: {},
    kind: 'company', roles: ['customer'], code: 'BODEGA-1900', name: 'Bodega 1900',
    legalName: 'Bodega 1900 SL', taxId: 'B65778899', locale: 'es',
    channels: [{ kind: 'telegram', address: '@bodega1900', label: null, primary: true, verifiedAt: iso(-40), optedOut: false }],
    people: [], addresses: [{
      id: 'a1', label: 'Local', line1: 'Carrer de Tamarit 91', line2: null, city: 'Barcelona',
      postcode: '08015', region: 'Catalunya', country: 'ES', lat: 41.377, lng: 2.157, accessNotes: null,
    }],
    deliveryPreferences: [{ addressId: 'a1', weekdays: ['tue', 'thu', 'sat'], window: { from: '08:00', to: '11:00' }, strict: false }],
    paymentTerms: terms(15, 3000), priceListId: 'pl_restaurants', ownerId: 'usr_lena' as never,
    referredByPartyId: null, supplier: null, ambassador: null, tags: ['tapas'], notes: null, status: 'active',
  },
  {
    ...audit, id: 'pty_canpuig' as Party['id'], businessId: BUSINESSES[0]!.id, custom: {},
    kind: 'company', roles: ['supplier', 'partner'], code: 'CAN-PUIG', name: 'Mas Can Puig',
    legalName: 'Mas Can Puig SCP', taxId: 'J17998877', locale: 'es',
    channels: [{ kind: 'whatsapp', address: '+34699887766', label: 'Quim', primary: true, verifiedAt: iso(-300), optedOut: false }],
    people: [{ id: 'p1', name: 'Quim Puig', role: 'Payés', channels: [], isDecisionMaker: true, notes: null }],
    addresses: [{
      id: 'a1', label: 'Finca', line1: 'Camí de la Riera s/n', line2: null, city: 'Sant Feliu de Buixalleu',
      postcode: '17451', region: 'Girona', country: 'ES', lat: 41.789, lng: 2.601, accessNotes: 'Pista de tierra 800 m.',
    }],
    deliveryPreferences: [],
    paymentTerms: terms(7), priceListId: null, ownerId: 'usr_jordi' as never, referredByPartyId: null,
    supplier: { sourceKind: 'farm', runDays: ['tue', 'fri'], standCode: null, orderCutoff: '17:00', leadTimeDays: 1 },
    ambassador: null, tags: ['ecológico', 'proximidad'], notes: 'Recogida propia. Salida 05:30 desde nave.', status: 'active',
  },
  {
    ...audit, id: 'pty_mercab_212' as Party['id'], businessId: BUSINESSES[0]!.id, custom: {},
    kind: 'company', roles: ['supplier'], code: 'FRUITES-VALLS', name: 'Fruites Valls',
    legalName: 'Fruites Valls SL', taxId: 'B08221144', locale: 'es',
    channels: [{ kind: 'phone', address: '+34934567890', label: 'Parada', primary: true, verifiedAt: null, optedOut: false }],
    people: [], addresses: [],
    deliveryPreferences: [],
    paymentTerms: terms(0), priceListId: null, ownerId: 'usr_jordi' as never, referredByPartyId: null,
    supplier: { sourceKind: 'market', runDays: ['mon', 'tue', 'wed', 'thu', 'fri', 'sat'], standCode: 'MB-212', orderCutoff: null, leadTimeDays: 0 },
    ambassador: null, tags: [], notes: null, status: 'active',
  },
  {
    ...audit, id: 'pty_mercab_054' as Party['id'], businessId: BUSINESSES[0]!.id, custom: {},
    kind: 'company', roles: ['supplier'], code: 'PEIX-ROIG', name: 'Peix Roig',
    legalName: 'Peix Roig SA', taxId: 'A08556677', locale: 'es',
    channels: [{ kind: 'whatsapp', address: '+34600445566', label: 'Parada', primary: true, verifiedAt: iso(-10), optedOut: false }],
    people: [], addresses: [],
    deliveryPreferences: [],
    paymentTerms: terms(0), priceListId: null, ownerId: 'usr_jordi' as never, referredByPartyId: null,
    supplier: { sourceKind: 'market', runDays: ['tue', 'wed', 'thu', 'fri'], standCode: 'MB-054', orderCutoff: null, leadTimeDays: 0 },
    ambassador: null, tags: ['pescado'], notes: 'Subasta a las 04:15. Llegar antes.', status: 'active',
  },
  {
    ...audit, id: 'pty_amb_carla' as Party['id'], businessId: BUSINESSES[0]!.id, custom: {},
    kind: 'person', roles: ['ambassador'], code: 'CARLA-M', name: 'Carla Mestres',
    legalName: null, taxId: '46778899K', locale: 'es',
    channels: [{ kind: 'whatsapp', address: '+34611223344', label: null, primary: true, verifiedAt: iso(-150), optedOut: false }],
    people: [], addresses: [], deliveryPreferences: [],
    paymentTerms: terms(30), priceListId: null, ownerId: 'usr_marta' as never, referredByPartyId: null,
    supplier: null, ambassador: { commissionBps: 300, durationMonths: 24 },
    tags: ['consultora'], notes: 'Trabaja con grupos de restauración del Eixample.', status: 'active',
  },
];

/* ------------------------------------------------------------------ *
 * Products
 * ------------------------------------------------------------------ */

const product = (
  id: string, sku: string, en: string, es: string, ru: string,
  hc: Product['handlingClass'], unit: Product['salesUnit'],
  extra: Partial<Product> = {},
): Product => ({
  ...audit,
  id: id as Product['id'],
  businessId: BUSINESSES[0]!.id,
  custom: {},
  sku,
  barcode: null,
  name: { en, es, ru },
  description: {},
  categoryId: null,
  unitMode: unit === 'kg' || unit === 'g' ? 'weight' : unit === 'l' ? 'volume' : 'each',
  salesUnit: unit,
  minQuantity: unit === 'kg' ? 0.5 : 1,
  quantityStep: unit === 'kg' ? 0.5 : 1,
  handlingClass: hc,
  taxRateKey: 'super-reduced',
  traits: {},
  defaultSupplierId: null,
  substituteIds: [],
  imageUrl: null,
  tags: [],
  status: 'active',
  ...extra,
});

export const PRODUCTS: Product[] = [
  product('prd_tom_raf', 'VG-TOM-RAF', 'Raf tomato', 'Tomate Raf', 'Томат Раф', 'fresh', 'kg', {
    defaultSupplierId: 'pty_mercab_212' as Product['defaultSupplierId'],
    substituteIds: ['prd_tom_pera'] as Product['substituteIds'],
    traits: {
      perishable: { shelfLifeDays: 6, minRemainingDays: 3, storageTempC: [8, 12] },
      origin: { country: 'ES', region: 'Almería', producerPartyId: null, certification: null },
      grading: { calibre: 'G', qualityClass: 'extra' },
      packaging: { unitsPerPackage: 5, packageLabel: 'crate', returnableDeposit: m(1.5) },
    },
    tags: ['temporada'],
  }),
  product('prd_tom_pera', 'VG-TOM-PER', 'Plum tomato', 'Tomate pera', 'Томат сливовидный', 'fresh', 'kg', {
    defaultSupplierId: 'pty_mercab_212' as Product['defaultSupplierId'],
    traits: {
      perishable: { shelfLifeDays: 8, minRemainingDays: 3, storageTempC: [8, 12] },
      origin: { country: 'ES', region: 'Múrcia', producerPartyId: null, certification: null },
      grading: { calibre: 'M', qualityClass: 'i' },
    },
  }),
  product('prd_cal_puig', 'VG-CAL-ECO', 'Calçots (organic)', 'Calçots ecológicos', 'Кальсоты (органик)', 'fresh', 'crate', {
    defaultSupplierId: 'pty_canpuig' as Product['defaultSupplierId'],
    minQuantity: 1, quantityStep: 1,
    traits: {
      perishable: { shelfLifeDays: 4, minRemainingDays: 2, storageTempC: [2, 6] },
      origin: { country: 'ES', region: 'Girona', producerPartyId: 'pty_canpuig' as never, certification: 'CCPAE' },
      packaging: { unitsPerPackage: 25, packageLabel: 'bundle', returnableDeposit: null },
    },
    tags: ['ecológico', 'temporada'],
  }),
  product('prd_gamba', 'PX-GAM-ROJ', 'Red prawn, Palamós', 'Gamba roja de Palamós', 'Красная креветка Паламос', 'live', 'kg', {
    defaultSupplierId: 'pty_mercab_054' as Product['defaultSupplierId'],
    taxRateKey: 'reduced',
    traits: {
      perishable: { shelfLifeDays: 1, minRemainingDays: 1, storageTempC: [0, 2] },
      origin: { country: 'ES', region: 'Costa Brava', producerPartyId: null, certification: null },
      grading: { calibre: '30/40', qualityClass: 'extra' },
      catchWeight: { nominalWeight: { value: 1, unit: 'kg' }, tolerancePct: 8 },
    },
  }),
  product('prd_merluza', 'PX-MER-PIN', 'Hake, line-caught', 'Merluza de pincho', 'Хек ярусного лова', 'chilled', 'kg', {
    defaultSupplierId: 'pty_mercab_054' as Product['defaultSupplierId'],
    taxRateKey: 'reduced',
    traits: {
      perishable: { shelfLifeDays: 2, minRemainingDays: 1, storageTempC: [0, 2] },
      catchWeight: { nominalWeight: { value: 2.5, unit: 'kg' }, tolerancePct: 12 },
      origin: { country: 'ES', region: 'Cantábrico', producerPartyId: null, certification: null },
    },
  }),
  product('prd_mant', 'LC-MANT-BLK', 'Cultured butter 82%', 'Mantequilla de elaboración 82%', 'Масло сливочное 82%', 'chilled', 'kg', {
    taxRateKey: 'reduced',
    traits: { perishable: { shelfLifeDays: 45, minRemainingDays: 14, storageTempC: [2, 6] } },
  }),
  product('prd_guis', 'CG-GUIS-500', 'Peas, IQF 2.5 kg', 'Guisante IQF 2,5 kg', 'Горошек ш/з 2,5 кг', 'frozen', 'box', {
    taxRateKey: 'reduced',
    traits: { packaging: { unitsPerPackage: 4, packageLabel: 'box', returnableDeposit: null } },
  }),
  product('prd_aoli', 'SC-AOVE-5L', 'Olive oil, arbequina 5 L', 'AOVE arbequina 5 L', 'Оливковое масло арбекина 5 л', 'ambient', 'pc', {
    taxRateKey: 'reduced',
    traits: { origin: { country: 'ES', region: 'Les Garrigues', producerPartyId: null, certification: 'DOP' } },
  }),
  product('prd_sal', 'SC-SAL-MAR', 'Sea salt, coarse 5 kg', 'Sal marina gruesa 5 kg', 'Соль морская крупная 5 кг', 'ambient', 'pc'),
];

const productById = new Map(PRODUCTS.map((p) => [p.id as string, p]));

/* ------------------------------------------------------------------ *
 * Market quotes — the price board
 * ------------------------------------------------------------------ */

const quote = (
  productId: string, cost: number, sell: number, prevSell: number | null,
  availability: MarketQuote['availability'], supplierId: string | null, note: string | null = null,
): MarketQuote => ({
  ...audit,
  id: `mq_${productId}_${day(0)}`,
  businessId: BUSINESSES[0]!.id,
  custom: {},
  productId: productId as MarketQuote['productId'],
  tradingDay: day(0),
  supplierId: supplierId as MarketQuote['supplierId'],
  cost: m(cost),
  sell: m(sell),
  previousSell: prevSell === null ? null : m(prevSell),
  availability,
  note,
});

export const QUOTES: MarketQuote[] = [
  quote('prd_tom_raf', 6.4, 8.9, 7.6, 'tight', 'pty_mercab_212', 'Subida por frío en Almería'),
  quote('prd_tom_pera', 1.35, 1.95, 1.95, 'plenty', 'pty_mercab_212'),
  quote('prd_cal_puig', 9.0, 13.5, 13.5, 'scarce', 'pty_canpuig', 'Última semana de temporada'),
  quote('prd_gamba', 78.0, 96.0, 88.0, 'scarce', 'pty_mercab_054', 'Poca entrada en lonja'),
  quote('prd_merluza', 11.2, 15.4, 16.8, 'plenty', 'pty_mercab_054'),
  quote('prd_mant', 9.8, 12.9, 12.9, 'plenty', null),
  quote('prd_guis', 7.4, 9.6, 9.6, 'plenty', null),
  quote('prd_aoli', 38.0, 46.5, 44.0, 'tight', null, 'Campaña corta'),
  quote('prd_sal', 3.1, 4.4, 4.4, 'plenty', null),
];

/* ------------------------------------------------------------------ *
 * Orders
 * ------------------------------------------------------------------ */

let lineSeq = 0;
const line = (
  productId: string, ordered: number, unitPrice: number,
  extra: Partial<OrderLine> = {},
): OrderLine => {
  const p = productById.get(productId)!;
  return {
    id: `oln_${++lineSeq}` as OrderLine['id'],
    productId: productId as OrderLine['productId'],
    productName: p.name['en'] ?? p.sku,
    sku: p.sku,
    ordered: { value: ordered, unit: p.salesUnit },
    picked: null,
    invoiced: null,
    unitPrice: m(unitPrice),
    priceSource: 'market',
    discountBps: 0,
    taxRateKey: p.taxRateKey,
    supplierId: null,
    supplierName: null,
    shortfall: null,
    substitution: null,
    note: null,
    sortOrder: lineSeq,
    ...extra,
  };
};

export const ORDERS: Order[] = [
  {
    ...audit, id: 'ord_1' as Order['id'], businessId: BUSINESSES[0]!.id, custom: {},
    number: '25-0891', status: 'picking',
    customerId: 'pty_sorli' as Order['customerId'], customerName: 'Restaurant Sorlí',
    channel: 'whatsapp', placedAt: iso(0, 5, 12),
    deliveryDate: day(0), deliveryWindow: { from: '07:00', to: '10:00' }, deliveryAddressId: 'a1',
    currency: EUR,
    lines: [
      line('prd_tom_raf', 12, 8.9, { picked: { value: 9, unit: 'kg' }, shortfall: { quantity: { value: 3, unit: 'kg' }, reason: 'supplier-short', note: 'Valls solo tenía 9 kg de calibre G', recordedBy: null, recordedAt: iso(0, 6, 5) } }),
      line('prd_gamba', 2, 96, { picked: { value: 2.06, unit: 'kg' }, invoiced: { value: 2.06, unit: 'kg' }, note: 'Pesar delante del cliente' }),
      line('prd_mant', 4, 12.9, { picked: { value: 4, unit: 'kg' } }),
      line('prd_aoli', 2, 46.5, { picked: { value: 2, unit: 'pc' } }),
    ],
    documentDiscountBps: 0, deliveryFee: null,
    assignedToId: 'usr_lena' as never, shipmentId: 'shp_1', invoiceId: null,
    customerNote: 'Los tomates que no estén muy maduros.', internalNote: null, tags: [],
  },
  {
    ...audit, id: 'ord_2' as Order['id'], businessId: BUSINESSES[0]!.id, custom: {},
    number: '25-0892', status: 'ready',
    customerId: 'pty_arts' as Order['customerId'], customerName: 'Hotel Arts — Cocina central',
    channel: 'portal', placedAt: iso(-1, 16, 40),
    deliveryDate: day(0), deliveryWindow: { from: '06:00', to: '08:00' }, deliveryAddressId: 'a1',
    currency: EUR,
    lines: [
      line('prd_tom_pera', 40, 1.85),
      line('prd_merluza', 18, 15.4, { picked: { value: 18.4, unit: 'kg' } }),
      line('prd_guis', 6, 9.6),
      line('prd_sal', 4, 4.4),
      line('prd_mant', 10, 12.4, { priceSource: 'personal' }),
    ],
    documentDiscountBps: 0, deliveryFee: m(12),
    assignedToId: 'usr_lena' as never, shipmentId: 'shp_2', invoiceId: null,
    customerNote: null, internalNote: 'Adjuntar registro de temperatura.', tags: ['hotel'],
  },
  {
    ...audit, id: 'ord_3' as Order['id'], businessId: BUSINESSES[0]!.id, custom: {},
    number: '25-0893', status: 'placed',
    customerId: 'pty_bodega' as Order['customerId'], customerName: 'Bodega 1900',
    channel: 'telegram', placedAt: iso(0, 6, 2),
    deliveryDate: day(1), deliveryWindow: { from: '08:00', to: '11:00' }, deliveryAddressId: 'a1',
    currency: EUR,
    lines: [
      line('prd_cal_puig', 6, 13.5),
      line('prd_tom_raf', 4, 8.9),
      line('prd_aoli', 1, 46.5),
    ],
    documentDiscountBps: 0, deliveryFee: null,
    assignedToId: null, shipmentId: 'shp_3', invoiceId: null,
    customerNote: 'Si no hay calçots, avisad antes de las 10.', internalNote: null, tags: [],
  },
  {
    ...audit, id: 'ord_4' as Order['id'], businessId: BUSINESSES[0]!.id, custom: {},
    number: '25-0888', status: 'delivered',
    customerId: 'pty_sorli' as Order['customerId'], customerName: 'Restaurant Sorlí',
    channel: 'recurring', placedAt: iso(-2, 17, 0),
    deliveryDate: day(-1), deliveryWindow: { from: '07:00', to: '10:00' }, deliveryAddressId: 'a1',
    currency: EUR,
    lines: [
      line('prd_tom_pera', 15, 1.95, { picked: { value: 15, unit: 'kg' }, invoiced: { value: 15, unit: 'kg' } }),
      line('prd_merluza', 8, 16.8, { picked: { value: 8.3, unit: 'kg' }, invoiced: { value: 8.3, unit: 'kg' } }),
    ],
    documentDiscountBps: 0, deliveryFee: null,
    assignedToId: 'usr_lena' as never, shipmentId: null, invoiceId: 'inv_1',
    customerNote: null, internalNote: null, tags: [],
  },
  {
    ...audit, id: 'ord_5' as Order['id'], businessId: BUSINESSES[0]!.id, custom: {},
    number: '25-0894', status: 'draft',
    customerId: 'pty_arts' as Order['customerId'], customerName: 'Hotel Arts — Cocina central',
    channel: 'sales-app', placedAt: null,
    deliveryDate: day(2), deliveryWindow: { from: '06:00', to: '08:00' }, deliveryAddressId: 'a1',
    currency: EUR,
    lines: [line('prd_gamba', 5, 96), line('prd_tom_raf', 20, 8.9)],
    documentDiscountBps: 0, deliveryFee: null,
    assignedToId: 'usr_lena' as never, shipmentId: null, invoiceId: null,
    customerNote: null, internalNote: 'Pendiente de confirmar cantidad de gamba.', tags: [],
  },
  {
    // Dry goods with no default supplier — the unassigned bucket the
    // grouping board exists to clear.
    ...audit, id: 'ord_6' as Order['id'], businessId: BUSINESSES[0]!.id, custom: {},
    number: '25-0895', status: 'placed',
    customerId: 'pty_bodega' as Order['customerId'], customerName: 'Bodega 1900',
    channel: 'phone', placedAt: iso(0, 7, 20),
    deliveryDate: day(0), deliveryWindow: { from: '08:00', to: '11:00' }, deliveryAddressId: 'a1',
    currency: EUR,
    lines: [
      line('prd_guis', 4, 9.6),
      line('prd_mant', 6, 12.9),
      line('prd_sal', 2, 4.4),
      line('prd_aoli', 1, 46.5, { note: 'De la campaña nueva si ya está.' }),
    ],
    documentDiscountBps: 0, deliveryFee: null,
    assignedToId: 'usr_lena' as never, shipmentId: null, invoiceId: null,
    customerNote: null, internalNote: null, tags: [],
  },
  {
    ...audit, id: 'ord_7' as Order['id'], businessId: BUSINESSES[0]!.id, custom: {},
    number: '25-0896', status: 'placed',
    customerId: 'pty_sorli' as Order['customerId'], customerName: 'Restaurant Sorlí',
    channel: 'whatsapp', placedAt: iso(0, 8, 5),
    deliveryDate: day(1), deliveryWindow: { from: '07:00', to: '10:00' }, deliveryAddressId: 'a1',
    currency: EUR,
    lines: [
      line('prd_tom_pera', 10, 1.95),
      line('prd_merluza', 6, 15.4, { note: 'Piezas de ~1,5 kg' }),
      line('prd_mant', 2, 12.9),
    ],
    documentDiscountBps: 0, deliveryFee: null,
    assignedToId: 'usr_lena' as never, shipmentId: 'shp_4', invoiceId: null,
    customerNote: null, internalNote: null, tags: [],
  },
];

/* ------------------------------------------------------------------ *
 * Purchasing
 * ------------------------------------------------------------------ */

export const PURCHASE_TASKS: PurchaseTask[] = [
  {
    ...audit, id: 'pt_1' as PurchaseTask['id'], businessId: BUSINESSES[0]!.id, custom: {},
    shoppingListId: 'sl_1', productId: 'prd_tom_raf' as never, productName: 'Raf tomato', sku: 'VG-TOM-RAF',
    supplierId: 'pty_mercab_212' as never, supplierName: 'Fruites Valls', standCode: 'MB-212',
    required: { value: 36, unit: 'kg' }, buffer: { value: 4, unit: 'kg' }, bought: { value: 29, unit: 'kg' },
    expectedCost: m(6.1), actualCost: m(6.4), status: 'partial',
    demandBreakdown: [
      { orderId: 'ord_1' as never, orderNumber: '25-0891', customerName: 'Restaurant Sorlí', quantity: { value: 12, unit: 'kg' } },
      { orderId: 'ord_3' as never, orderNumber: '25-0893', customerName: 'Bodega 1900', quantity: { value: 4, unit: 'kg' } },
      { orderId: 'ord_5' as never, orderNumber: '25-0894', customerName: 'Hotel Arts', quantity: { value: 20, unit: 'kg' } },
    ],
    note: 'Solo quedaba calibre G', completedAt: iso(0, 5, 40), sortOrder: 1,
  },
  {
    ...audit, id: 'pt_2' as PurchaseTask['id'], businessId: BUSINESSES[0]!.id, custom: {},
    shoppingListId: 'sl_1', productId: 'prd_gamba' as never, productName: 'Red prawn, Palamós', sku: 'PX-GAM-ROJ',
    supplierId: 'pty_mercab_054' as never, supplierName: 'Peix Roig', standCode: 'MB-054',
    required: { value: 7, unit: 'kg' }, buffer: null, bought: { value: 7, unit: 'kg' },
    expectedCost: m(72), actualCost: m(78), status: 'bought',
    demandBreakdown: [
      { orderId: 'ord_1' as never, orderNumber: '25-0891', customerName: 'Restaurant Sorlí', quantity: { value: 2, unit: 'kg' } },
      { orderId: 'ord_5' as never, orderNumber: '25-0894', customerName: 'Hotel Arts', quantity: { value: 5, unit: 'kg' } },
    ],
    note: null, completedAt: iso(0, 4, 30), sortOrder: 2,
  },
  {
    ...audit, id: 'pt_3' as PurchaseTask['id'], businessId: BUSINESSES[0]!.id, custom: {},
    shoppingListId: 'sl_1', productId: 'prd_merluza' as never, productName: 'Hake, line-caught', sku: 'PX-MER-PIN',
    supplierId: 'pty_mercab_054' as never, supplierName: 'Peix Roig', standCode: 'MB-054',
    required: { value: 18, unit: 'kg' }, buffer: { value: 2, unit: 'kg' }, bought: null,
    expectedCost: m(11.2), actualCost: null, status: 'todo',
    demandBreakdown: [
      { orderId: 'ord_2' as never, orderNumber: '25-0892', customerName: 'Hotel Arts', quantity: { value: 18, unit: 'kg' } },
    ],
    note: null, completedAt: null, sortOrder: 3,
  },
  {
    ...audit, id: 'pt_4' as PurchaseTask['id'], businessId: BUSINESSES[0]!.id, custom: {},
    shoppingListId: 'sl_1', productId: 'prd_cal_puig' as never, productName: 'Calçots (organic)', sku: 'VG-CAL-ECO',
    supplierId: 'pty_canpuig' as never, supplierName: 'Mas Can Puig', standCode: null,
    required: { value: 6, unit: 'crate' }, buffer: null, bought: null,
    expectedCost: m(9), actualCost: null, status: 'todo',
    demandBreakdown: [
      { orderId: 'ord_3' as never, orderNumber: '25-0893', customerName: 'Bodega 1900', quantity: { value: 6, unit: 'crate' } },
    ],
    note: 'Salida a finca 05:30', completedAt: null, sortOrder: 4,
  },
  {
    ...audit, id: 'pt_5' as PurchaseTask['id'], businessId: BUSINESSES[0]!.id, custom: {},
    shoppingListId: 'sl_1', productId: 'prd_tom_pera' as never, productName: 'Plum tomato', sku: 'VG-TOM-PER',
    supplierId: 'pty_mercab_212' as never, supplierName: 'Fruites Valls', standCode: 'MB-212',
    required: { value: 40, unit: 'kg' }, buffer: { value: 5, unit: 'kg' }, bought: { value: 45, unit: 'kg' },
    expectedCost: m(1.3), actualCost: m(1.35), status: 'bought',
    demandBreakdown: [
      { orderId: 'ord_2' as never, orderNumber: '25-0892', customerName: 'Hotel Arts', quantity: { value: 40, unit: 'kg' } },
    ],
    note: null, completedAt: iso(0, 5, 10), sortOrder: 5,
  },
];

/* ------------------------------------------------------------------ *
 * Logistics
 * ------------------------------------------------------------------ */

export const SHIPMENTS: Shipment[] = [
  {
    ...audit, id: 'shp_1' as Shipment['id'], businessId: BUSINESSES[0]!.id, custom: {},
    number: 'ALB-25-1140', routeId: 'rt_1',
    customerId: 'pty_sorli' as never, customerName: 'Restaurant Sorlí',
    addressId: 'a1', addressLabel: 'Provença 288, Barcelona', orderIds: ['ord_1' as never],
    date: day(0), window: { from: '07:00', to: '10:00' }, sequence: 2, status: 'pending',
    etaAt: iso(0, 8, 15), arrivedAt: null, completedAt: null,
    crateCount: 6, cratesReturned: 0, proof: null, failureReason: null, note: null,
  },
  {
    ...audit, id: 'shp_2' as Shipment['id'], businessId: BUSINESSES[0]!.id, custom: {},
    number: 'ALB-25-1141', routeId: 'rt_1',
    customerId: 'pty_arts' as never, customerName: 'Hotel Arts — Cocina central',
    addressId: 'a1', addressLabel: 'Marina 19, muelle -2', orderIds: ['ord_2' as never],
    date: day(0), window: { from: '06:00', to: '08:00' }, sequence: 1, status: 'delivered',
    etaAt: iso(0, 6, 40), arrivedAt: iso(0, 6, 48), completedAt: iso(0, 7, 5),
    crateCount: 14, cratesReturned: 11,
    proof: { signedBy: 'S. Marchetti', signatureUrl: null, photoUrls: [], lat: 41.386, lng: 2.196, capturedAt: iso(0, 7, 5) },
    failureReason: null, note: null,
  },
  {
    ...audit, id: 'shp_3' as Shipment['id'], businessId: BUSINESSES[0]!.id, custom: {},
    number: 'ALB-25-1142', routeId: 'rt_2',
    customerId: 'pty_bodega' as never, customerName: 'Bodega 1900',
    addressId: 'a1', addressLabel: 'Tamarit 91, Barcelona', orderIds: ['ord_3' as never],
    date: day(1), window: { from: '08:00', to: '11:00' }, sequence: 1, status: 'pending',
    etaAt: iso(1, 8, 40), arrivedAt: null, completedAt: null,
    crateCount: 5, cratesReturned: 0, proof: null, failureReason: null,
    note: 'Timbre "almacén", preguntar por Marc.',
  },
  {
    // No route yet — the pile on the dock that tomorrow's planning clears.
    ...audit, id: 'shp_4' as Shipment['id'], businessId: BUSINESSES[0]!.id, custom: {},
    number: 'ALB-25-1143', routeId: null,
    customerId: 'pty_sorli' as never, customerName: 'Restaurant Sorlí',
    addressId: 'a1', addressLabel: 'Provença 288, Barcelona', orderIds: ['ord_7' as never],
    date: day(1), window: { from: '07:00', to: '10:00' }, sequence: 0, status: 'pending',
    etaAt: null, arrivedAt: null, completedAt: null,
    crateCount: 3, cratesReturned: 0, proof: null, failureReason: null, note: null,
  },
];

export const ROUTES: Route[] = [
  {
    ...audit, id: 'rt_1' as Route['id'], businessId: BUSINESSES[0]!.id, custom: {},
    name: 'Eixample AM', date: day(0), vehicleId: 'veh_1', driverId: 'usr_paco' as never,
    carrierPartyId: null, status: 'dispatched', departsAt: iso(0, 6, 15), returnedAt: null,
    shipmentIds: ['shp_2' as never, 'shp_1' as never], note: null,
  },
  {
    ...audit, id: 'rt_2' as Route['id'], businessId: BUSINESSES[0]!.id, custom: {},
    name: 'Sants + Poble Sec', date: day(1), vehicleId: 'veh_2', driverId: null,
    carrierPartyId: null, status: 'planning', departsAt: null, returnedAt: null,
    shipmentIds: ['shp_3' as never], note: null,
  },
];

/* ------------------------------------------------------------------ *
 * Billing
 * ------------------------------------------------------------------ */

export const INVOICES: Invoice[] = [
  {
    ...audit, id: 'inv_1' as Invoice['id'], businessId: BUSINESSES[0]!.id, custom: {},
    direction: 'sales', series: 'A', number: 'A-25-0412', status: 'part-paid',
    partyId: 'pty_sorli' as never, partyName: 'Restaurant Sorlí', partyTaxId: 'B60112233',
    billingAddress: 'Carrer de Provença 288, 08008 Barcelona',
    issueDate: day(-1), dueDate: day(29), currency: EUR, exchangeRate: 1,
    orderIds: ['ord_4' as never],
    lines: [
      { id: 'il1', productId: 'prd_tom_pera', description: 'Tomate pera', quantity: { value: 15, unit: 'kg' }, unitPrice: m(1.95), discountBps: 0, taxRateKey: 'super-reduced', taxBps: 400, net: m(29.25), tax: m(1.17), surcharge: null },
      { id: 'il2', productId: 'prd_merluza', description: 'Merluza de pincho', quantity: { value: 8.3, unit: 'kg' }, unitPrice: m(16.8), discountBps: 0, taxRateKey: 'reduced', taxBps: 1000, net: m(139.44), tax: m(13.94), surcharge: null },
    ],
    net: m(168.69), tax: m(15.11), surcharge: null, gross: m(183.8), paid: m(100),
    pdfUrl: null, sentAt: iso(-1, 12), note: null, correctsInvoiceId: null,
  },
  {
    ...audit, id: 'inv_2' as Invoice['id'], businessId: BUSINESSES[0]!.id, custom: {},
    direction: 'sales', series: 'A', number: 'A-25-0388', status: 'overdue',
    partyId: 'pty_bodega' as never, partyName: 'Bodega 1900', partyTaxId: 'B65778899',
    billingAddress: 'Carrer de Tamarit 91, 08015 Barcelona',
    issueDate: day(-52), dueDate: day(-37), currency: EUR, exchangeRate: 1, orderIds: [],
    lines: [{ id: 'il1', productId: null, description: 'Pedidos de marzo', quantity: { value: 1, unit: 'pc' }, unitPrice: m(1240), discountBps: 0, taxRateKey: 'super-reduced', taxBps: 400, net: m(1240), tax: m(49.6), surcharge: null }],
    net: m(1240), tax: m(49.6), surcharge: null, gross: m(1289.6), paid: m(0),
    pdfUrl: null, sentAt: iso(-52, 10), note: 'Reclamado 2 veces.', correctsInvoiceId: null,
  },
  {
    ...audit, id: 'inv_3' as Invoice['id'], businessId: BUSINESSES[0]!.id, custom: {},
    direction: 'purchase', series: 'REC', number: 'FV-2211', status: 'issued',
    partyId: 'pty_mercab_212' as never, partyName: 'Fruites Valls', partyTaxId: 'B08221144',
    billingAddress: 'Mercabarna, parada 212',
    issueDate: day(0), dueDate: day(0), currency: EUR, exchangeRate: 1, orderIds: [],
    lines: [{ id: 'il1', productId: 'prd_tom_raf', description: 'Tomate Raf calibre G', quantity: { value: 29, unit: 'kg' }, unitPrice: m(6.4), discountBps: 0, taxRateKey: 'super-reduced', taxBps: 400, net: m(185.6), tax: m(7.42), surcharge: null }],
    net: m(185.6), tax: m(7.42), surcharge: null, gross: m(193.02), paid: m(0),
    pdfUrl: null, sentAt: null, note: null, correctsInvoiceId: null,
  },
  {
    ...audit, id: 'inv_4' as Invoice['id'], businessId: BUSINESSES[0]!.id, custom: {},
    direction: 'sales', series: 'A', number: 'A-25-0405', status: 'sent',
    partyId: 'pty_arts' as never, partyName: 'Hotel Arts — Cocina central', partyTaxId: 'A08334455',
    billingAddress: 'Carrer de la Marina 19, 08005 Barcelona',
    issueDate: day(-12), dueDate: day(48), currency: EUR, exchangeRate: 1, orderIds: [],
    lines: [{ id: 'il1', productId: null, description: 'Suministro semana 14', quantity: { value: 1, unit: 'pc' }, unitPrice: m(4820.5), discountBps: 0, taxRateKey: 'super-reduced', taxBps: 400, net: m(4820.5), tax: m(192.82), surcharge: null }],
    net: m(4820.5), tax: m(192.82), surcharge: null, gross: m(5013.32), paid: m(0),
    pdfUrl: null, sentAt: iso(-12, 11), note: null, correctsInvoiceId: null,
  },
];

export const PAYMENTS: Payment[] = [
  {
    ...audit, id: 'pay_1' as Payment['id'], businessId: BUSINESSES[0]!.id, custom: {},
    direction: 'in', partyId: 'pty_sorli' as never, partyName: 'Restaurant Sorlí',
    date: day(-1), amount: m(100), method: 'transfer', reference: 'TRF-99120',
    allocations: [{ invoiceId: 'inv_1' as never, invoiceNumber: 'A-25-0412', amount: m(100) }],
    status: 'cleared', note: null,
  },
  {
    ...audit, id: 'pay_2' as Payment['id'], businessId: BUSINESSES[0]!.id, custom: {},
    direction: 'out', partyId: 'pty_mercab_054' as never, partyName: 'Peix Roig',
    date: day(0), amount: m(546), method: 'cash', reference: null,
    allocations: [], status: 'cleared', note: 'Pago en parada',
  },
  {
    ...audit, id: 'pay_3' as Payment['id'], businessId: BUSINESSES[0]!.id, custom: {},
    direction: 'in', partyId: 'pty_arts' as never, partyName: 'Hotel Arts — Cocina central',
    date: day(-3), amount: m(2500), method: 'transfer', reference: 'SEPA-4471',
    allocations: [], status: 'cleared', note: 'Sin aplicar — pendiente de casar con facturas',
  },
];

/* ------------------------------------------------------------------ *
 * Stock
 * ------------------------------------------------------------------ */

export const STOCK_LOTS: StockLot[] = [
  {
    ...audit, id: 'lot_1' as StockLot['id'], businessId: BUSINESSES[0]!.id, custom: {},
    productId: 'prd_tom_raf' as never, productName: 'Raf tomato', sku: 'VG-TOM-RAF',
    lotCode: 'VLL-2291', supplierId: 'pty_mercab_212' as never, location: 'Cámara 2 · E3',
    received: { value: 29, unit: 'kg' }, remaining: { value: 17, unit: 'kg' }, reserved: { value: 4, unit: 'kg' },
    receivedAt: iso(0, 6, 0), bestBefore: day(5), unitCost: m(6.4), status: 'available',
  },
  {
    ...audit, id: 'lot_2' as StockLot['id'], businessId: BUSINESSES[0]!.id, custom: {},
    productId: 'prd_mant' as never, productName: 'Cultured butter 82%', sku: 'LC-MANT-BLK',
    lotCode: 'MB-8841', supplierId: null, location: 'Cámara 1 · A1',
    received: { value: 60, unit: 'kg' }, remaining: { value: 34, unit: 'kg' }, reserved: { value: 14, unit: 'kg' },
    receivedAt: iso(-6, 8), bestBefore: day(12), unitCost: m(9.8), status: 'available',
  },
  {
    ...audit, id: 'lot_3' as StockLot['id'], businessId: BUSINESSES[0]!.id, custom: {},
    productId: 'prd_guis' as never, productName: 'Peas, IQF 2.5 kg', sku: 'CG-GUIS-500',
    lotCode: null, supplierId: null, location: 'Congelador · C2',
    received: { value: 40, unit: 'box' }, remaining: { value: 22, unit: 'box' }, reserved: { value: 6, unit: 'box' },
    receivedAt: iso(-20, 9), bestBefore: day(240), unitCost: m(7.4), status: 'available',
  },
  {
    ...audit, id: 'lot_4' as StockLot['id'], businessId: BUSINESSES[0]!.id, custom: {},
    productId: 'prd_tom_pera' as never, productName: 'Plum tomato', sku: 'VG-TOM-PER',
    lotCode: 'VLL-2288', supplierId: 'pty_mercab_212' as never, location: 'Cámara 2 · E1',
    received: { value: 45, unit: 'kg' }, remaining: { value: 3.5, unit: 'kg' }, reserved: null,
    receivedAt: iso(0, 6, 0), bestBefore: day(2), unitCost: m(1.35), status: 'available',
  },
];

/* ------------------------------------------------------------------ *
 * Messaging
 * ------------------------------------------------------------------ */

export const THREADS: Thread[] = [
  {
    ...audit, id: 'thr_1' as Thread['id'], businessId: BUSINESSES[0]!.id, custom: {},
    partyId: 'pty_sorli' as never, partyName: 'Restaurant Sorlí',
    channels: ['whatsapp', 'email'], subject: null,
    lastMessageAt: iso(0, 6, 12), lastMessagePreview: 'Vale, mándame 9 y lo cuadramos mañana',
    lastMessageChannel: 'whatsapp', unreadCount: 0, assignedToId: 'usr_lena' as never,
    status: 'open', snoozedUntil: null, tags: [],
  },
  {
    ...audit, id: 'thr_2' as Thread['id'], businessId: BUSINESSES[0]!.id, custom: {},
    partyId: 'pty_bodega' as never, partyName: 'Bodega 1900',
    channels: ['telegram'], subject: null,
    lastMessageAt: iso(0, 6, 2), lastMessagePreview: '6 cajas de calçots para mañana',
    lastMessageChannel: 'telegram', unreadCount: 2, assignedToId: null,
    status: 'open', snoozedUntil: null, tags: ['pedido'],
  },
  {
    ...audit, id: 'thr_3' as Thread['id'], businessId: BUSINESSES[0]!.id, custom: {},
    partyId: 'pty_mercab_054' as never, partyName: 'Peix Roig',
    channels: ['whatsapp'], subject: null,
    lastMessageAt: iso(-1, 20, 30), lastMessagePreview: 'Mañana poca gamba, avisa temprano',
    lastMessageChannel: 'whatsapp', unreadCount: 0, assignedToId: 'usr_jordi' as never,
    status: 'open', snoozedUntil: null, tags: [],
  },
];

export const MESSAGES: Message[] = [
  { id: 'msg_1', threadId: 'thr_1' as never, channel: 'whatsapp', direction: 'in', status: 'read', authorId: null, authorName: 'Aleix Ferrer', body: 'Buenos días! ¿Cómo va el Raf hoy?', attachments: [], transcript: null, relatedOrderId: 'ord_1' as never, templateKey: null, sentAt: iso(0, 5, 58), failureReason: null },
  { id: 'msg_2', threadId: 'thr_1' as never, channel: 'whatsapp', direction: 'out', status: 'read', authorId: 'usr_lena' as never, authorName: 'Lena Kovalenko', body: 'Buenos días Aleix. Hoy Valls solo tenía 9 kg de calibre G, a 8,90 €. ¿Te los mando o esperamos a mañana?', attachments: [], transcript: null, relatedOrderId: 'ord_1' as never, templateKey: null, sentAt: iso(0, 6, 4), failureReason: null },
  { id: 'msg_3', threadId: 'thr_1' as never, channel: 'whatsapp', direction: 'in', status: 'read', authorId: null, authorName: 'Aleix Ferrer', body: 'Vale, mándame 9 y lo cuadramos mañana', attachments: [], transcript: 'Vale, mándame nueve y lo cuadramos mañana', relatedOrderId: 'ord_1' as never, templateKey: null, sentAt: iso(0, 6, 12), failureReason: null },
  { id: 'msg_4', threadId: 'thr_2' as never, channel: 'telegram', direction: 'in', status: 'delivered', authorId: null, authorName: 'Bodega 1900', body: '6 cajas de calçots para mañana', attachments: [], transcript: null, relatedOrderId: 'ord_3' as never, templateKey: null, sentAt: iso(0, 6, 2), failureReason: null },
];

/* ------------------------------------------------------------------ *
 * Schedule
 * ------------------------------------------------------------------ */

export const APPOINTMENTS: Appointment[] = [
  { ...audit, id: 'apt_1' as Appointment['id'], businessId: BUSINESSES[0]!.id, custom: {}, title: 'Cata de aceite nueva campaña', kind: 'tasting', partyId: 'pty_sorli' as never, partyName: 'Restaurant Sorlí', attendeeIds: ['usr_lena' as never], startsAt: iso(1, 16), endsAt: iso(1, 17, 30), location: 'Provença 288', relatedOrderId: null, status: 'confirmed', reminderMinutesBefore: 60, note: null },
  { ...audit, id: 'apt_2' as Appointment['id'], businessId: BUSINESSES[0]!.id, custom: {}, title: 'Salida a Can Puig', kind: 'farm-visit', partyId: 'pty_canpuig' as never, partyName: 'Mas Can Puig', attendeeIds: ['usr_jordi' as never], startsAt: iso(0, 4, 30), endsAt: iso(0, 8), location: 'Sant Feliu de Buixalleu', relatedOrderId: null, status: 'scheduled', reminderMinutesBefore: 30, note: 'Llevar cajas vacías.' },
  { ...audit, id: 'apt_3' as Appointment['id'], businessId: BUSINESSES[0]!.id, custom: {}, title: 'Revisión de tarifa 2025', kind: 'sales-visit', partyId: 'pty_arts' as never, partyName: 'Hotel Arts', attendeeIds: ['usr_marta' as never, 'usr_lena' as never], startsAt: iso(3, 11), endsAt: iso(3, 12), location: 'Marina 19', relatedOrderId: null, status: 'scheduled', reminderMinutesBefore: 120, note: null },
];
